import React from 'react';
import Header from './Header';
import SideNav from './SideNav';
import Footer from './Footer';
import './BrainWarmup.css';
import './ExamPrep.css';
import './JapaneseExamPrep.css';
import './JapaneseExamQuestions.css';

// Per-section intro / gate screen shown before each section starts.
// Matches the Figma "Sec A/B/C landing page Exam Prep" frames exactly.
// The attempt is created only when the user clicks Start here.
function fmtClock(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

const JapaneseSectionIntro = ({
  user, levelCode, section, meta, durationMin, starting, loadError,
  completedMeta, breakActive, breakLeft, breakTotalSecs, onStart, onExit, attemptsLeft,
}) => {
  const sectionPassMark = section.passMarks || 19;

  // Attempts chip: "2 attempts every 24 hours" / "N attempts left for today"
  // (1 = orange, 0 = red), per Figma.
  const left = typeof attemptsLeft === 'number' ? attemptsLeft : 2;
  const attemptsChip = (extraCls = '') => (
    <span className={'jeq-chip' + extraCls + (left >= 2 ? '' : left === 1 ? ' jep-attempts-warn' : ' jep-attempts-none')}>
      <span className="jeq-chip-ico">⟳</span>
      {left >= 2 ? <><strong>2</strong> attempts every 24 hours</> : <><strong>{left}</strong> attempts left for today</>}
    </span>
  );

  const sequenceParts = [
    { key: 'vocab', label: 'Character & Vocabulary' },
    { key: 'grammar_reading', label: 'Grammar and Reading' },
    { key: 'listening', label: 'Listening' },
  ];

  const breakPct = breakTotalSecs ? Math.max(0, Math.min(100, (breakLeft / breakTotalSecs) * 100)) : 0;

  return (
    <div className="exam-prep-page jep-page jeq-page jeq-intro-page">
      <Header user={user} />
      <SideNav />

      <main className={`jeq-intro-main${completedMeta ? ' jeq-intro-between' : ''}`}>
        {/* Completed banner for the section just submitted (between sections). */}
        {completedMeta && (
          <div className="jeq-intro-completed">
            <h2 className="jeq-intro-completed-title">
              <span className="jeq-intro-completed-word">Completed</span>
              <span className="jeq-intro-completed-rest"> - Section {completedMeta.letter} : {completedMeta.fullName}</span>
            </h2>
            <p className="jeq-intro-completed-sub">
              Your {completedMeta.shortName} responses have been recorded. <strong>Section {completedMeta.letter} is now locked.</strong> You cannot return to or change your answers in this section.
            </p>
          </div>
        )}

        {/* Sequence line (outside the band). Label = black; completed = green (with
            its trailing arrow green); current = purple; upcoming = grey. */}
        <p className="jeq-intro-sequence">
          <span className="jeq-seq-label">{completedMeta ? 'Next in sequence will be: ' : 'Sequence will be: '}</span>
          {(() => {
            // Every section BEFORE the current one is completed (green). The current
            // section is purple; anything after is grey. This makes both A and B show
            // green with green arrows when the user is on Section C.
            const currentIdx = sequenceParts.findIndex((p) => p.key === section.key);
            return sequenceParts.map((p, i) => {
              const isDone = currentIdx > -1 && i < currentIdx;
              const isCurrent = p.key === section.key;
              const partCls = isDone ? 'jeq-seq-done' : isCurrent ? 'jeq-seq-current' : 'jeq-seq-upcoming';
              // The arrow AFTER a completed section is green too.
              const arrowCls = isDone ? 'jeq-seq-arrow jeq-seq-arrow-done' : 'jeq-seq-arrow';
              return (
                <React.Fragment key={p.key}>
                  <span className={partCls}>{p.label}</span>
                  {i < sequenceParts.length - 1 && <span className={arrowCls}>{' → '}</span>}
                </React.Fragment>
              );
            });
          })()}
        </p>

        {/* Band: for the between-sections screen the heading/description/break live
            INSIDE the bordered box (per SS). For the first (Section A) intro they sit
            above the band as before. */}
        {completedMeta ? (
          <div className="jeq-intro-band jeq-intro-band-full">
            <h1 className="jeq-intro-heading">
              <span className="jeq-intro-heading-sec">Section {meta.letter} : </span>
              <span className="jeq-intro-heading-name">{meta.fullName}</span>
            </h1>
            <p className="jeq-intro-desc">{meta.description}</p>

            {breakActive && (
              <div className="jeq-intro-break">
                <div className="jeq-intro-break-row">
                  <span className="jeq-intro-break-label">Break Left <strong>{fmtClock(breakLeft)}</strong></span>
                  <span className="jeq-intro-break-bar"><span className="jeq-intro-break-fill" style={{ width: `${breakPct}%` }} /></span>
                </div>
                <p className="jeq-intro-break-note">
                  {Math.round((breakTotalSecs || 0) / 60)}-minute break. Start the next section before time runs out - it will auto-start when the break ends.
                </p>
              </div>
            )}

            <div className="jeq-intro-chips jeq-intro-chips-inside">
              <span className="jeq-chip"><span className="jeq-chip-ico">?</span>{section.totalQuestions} questions</span>
              <span className="jeq-chip"><span className="jeq-chip-ico">◷</span>{durationMin} min <strong className="jeq-chip-amber">(Auto-submits at expiry)</strong></span>
              <span className="jeq-chip"><span className="jeq-chip-ico">▤</span>{section.totalMarks} marks</span>
              <span className="jeq-chip"><span className="jeq-chip-ico">✓</span>Passing Marks- {sectionPassMark}</span>
              {attemptsChip()}
            </div>

            <div className="jeq-intro-instructions">
              <h3 className="jeq-intro-instructions-title">Section {meta.letter} Instructions</h3>
              <ul>
                <li>All {section.totalQuestions} questions are <strong>multiple-choice questions (MCQs).</strong></li>
                <li>Select <strong>one answer</strong> for each question.</li>
                <li>You may move between questions within this section and review your answers before submitting.</li>
                <li>Make sure all questions are answered and the section is submitted within {durationMin} minutes.</li>
                <li>At the end of {durationMin} minutes, the section will be automatically submitted.</li>
                <li>Complete sections in order — you cannot skip or go back.</li>
              </ul>
            </div>

            <div className="jeq-intro-start-row jeq-intro-start-inside">
              <button className="jeq-intro-start-btn" onClick={onStart} disabled={starting}>
                {starting ? 'Starting…' : <>Start Section {meta.letter} - {meta.startName}<span className="jeq-intro-start-arrow">→</span></>}
              </button>
            </div>
          </div>
        ) : (
          <>
            <h1 className="jeq-intro-heading">
              <span className="jeq-intro-heading-sec">Section {meta.letter} : </span>
              <span className="jeq-intro-heading-name">{meta.fullName}</span>
            </h1>
            <p className="jeq-intro-desc">{meta.description}</p>

            <div className="jeq-intro-band">
              <div className="jeq-intro-chips">
                <span className="jeq-chip"><span className="jeq-chip-ico">?</span>{section.totalQuestions} questions</span>
                <span className="jeq-chip"><span className="jeq-chip-ico">◷</span>{durationMin} min <strong className="jeq-chip-amber">(Auto-submits at expiry)</strong></span>
                <span className="jeq-chip"><span className="jeq-chip-ico">▤</span>{section.totalMarks} marks</span>
                <span className="jeq-chip"><span className="jeq-chip-ico">✓</span>Passing Marks- {sectionPassMark}</span>
                {attemptsChip()}
              </div>

              <div className="jeq-intro-instructions">
                <h3 className="jeq-intro-instructions-title">Section {meta.letter} Instructions</h3>
                <ul>
                  <li>All {section.totalQuestions} questions are <strong>multiple-choice questions (MCQs).</strong></li>
                  <li>Select <strong>one answer</strong> for each question.</li>
                  <li>You may move between questions within this section and review your answers before submitting.</li>
                  <li>Make sure all questions are answered and the section is submitted within {durationMin} minutes.</li>
                  <li>At the end of {durationMin} minutes, the section will be automatically submitted.</li>
                </ul>
              </div>

              <div className="jeq-intro-start-row">
                <button className="jeq-intro-start-btn" onClick={onStart} disabled={starting}>
                  {starting ? 'Starting…' : <>Start Section {meta.letter} - {meta.startName}<span className="jeq-intro-start-arrow">→</span></>}
                </button>
              </div>
            </div>
          </>
        )}

        {/* Warning */}
        <div className="jeq-intro-warning">
          <span className="jeq-intro-warning-ico">⚠</span>
          <span>
            <strong>Timer starts immediately</strong> when you click Start. The {durationMin}-minute clock cannot be paused. Make sure you are ready before proceeding.
          </span>
        </div>

        {loadError ? <p className="jeq-intro-error">{loadError}</p> : null}
      </main>

      <Footer />

      {/* Loading popup while the agent generates the exam (same as other exam pages) */}
      {starting && (
        <div className="bw-confirm-overlay">
          <div className="ep-gen-loading">
            <img src="/images/loadingicon.svg" alt="" className="ep-submitting-icon" />
            <h3 className="ep-gen-loading-title">Hold on a moment!</h3>
            <p className="ep-gen-loading-text">We&#8217;re getting your questions ready. Your exam is almost ready to begin.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default JapaneseSectionIntro;
